import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

export interface CommsLogFilters {
  channel?: "call" | "text" | "email" | "note" | undefined;
  direction?: "inbound" | "outbound" | "internal" | undefined;
  status?: string | undefined;
  memberId?: string | undefined;
  /** A phone number in any format; the server reads it in the club's country. */
  number?: string | undefined;
  unmatched?: boolean | undefined;
  /** ISO date-times with a zone. `until` is exclusive. */
  since?: string | undefined;
  until?: string | undefined;
}

export interface CommsMessageFilters {
  state?: "open" | "done" | undefined;
  urgency?: "low" | "normal" | "urgent" | undefined;
  memberId?: string | undefined;
  recipientUserId?: string | undefined;
  recipientRoleId?: string | undefined;
}

/** The communications module: the call and text log, unknown callers, phone messages and the phone-number settings. */
export function communicationsEndpoints({ http, get, post, patch, del, paged }: Helpers) {
  /** A phone number as a path segment: any format, with `+` and spaces encoded. */
  const numberPath = (number: string) => encodeURIComponent(number);
  return {
    personalMailAccount: (signal?: AbortSignal) => get<Out<"communications_status">>("/communications/mail/account", { signal }),
    connectPersonalMail: (input: In<"communications_connect">) => post<Out<"communications_connect">>("/communications/mail/connect", input),
    disconnectPersonalMail: () => del("/communications/mail/account"),
    sendPersonalMail: (input: In<"communications_send">) => post<Out<"communications_send">>("/communications/mail/send", input),
    listLog: (page: PageRequest & CommsLogFilters = {}) =>
      get<Out<"communications_list_log">>(
        "/communications/log",
        paged(page, {
          channel: page.channel,
          direction: page.direction,
          status: page.status,
          member_id: page.memberId,
          number: page.number,
          unmatched: page.unmatched,
          since: page.since,
          until: page.until,
        }),
      ),
    createLogEntry: (input: In<"communications_create_log_entry">) => post<Out<"communications_create_log_entry">>("/communications/log", input),
    getLogEntry: (id: string, signal?: AbortSignal) => get<Out<"communications_get_log_entry">>(`/communications/log/${id}`, { signal }),
    /** `member_id: null` takes the link away. */
    linkLogEntry: (id: string, input: In<"communications_link_log_entry">) => post<Out<"communications_link_log_entry">>(`/communications/log/${id}/link`, input),

    /** A member's calls and texts, newest first. `household` also lists rows linked to anyone in their household. */
    memberTimeline: (memberId: string, page: PageRequest & { household?: boolean | undefined } = {}) =>
      get<Out<"communications_member_timeline">>(`/communications/timeline/${memberId}`, paged(page, { household: page.household })),

    listUnknown: (page: PageRequest = {}) => get<Out<"communications_list_unknown">>("/communications/unknown", paged(page)),
    linkUnknown: (input: In<"communications_link_unknown">) => post<Out<"communications_link_unknown">>("/communications/unknown/link", input),

    listMessages: (page: PageRequest & CommsMessageFilters = {}) =>
      get<Out<"communications_list_messages">>(
        "/communications/messages",
        paged(page, {
          state: page.state,
          urgency: page.urgency,
          member_id: page.memberId,
          recipient_user_id: page.recipientUserId,
          recipient_role_id: page.recipientRoleId,
        }),
      ),
    listMyMessages: (page: PageRequest & { state?: "open" | "done" | undefined } = {}) =>
      get<Out<"communications_list_my_messages">>("/communications/messages/mine", paged(page, { state: page.state })),
    /** Staff users and roles a message can be left for (needs only the permission to take messages). */
    listRecipients: (signal?: AbortSignal) => get<Out<"communications_list_recipients">>("/communications/recipients", { signal }),
    createMessage: (input: In<"communications_create_message">) => post<Out<"communications_create_message">>("/communications/messages", input),
    getMessage: (id: string, signal?: AbortSignal) => get<Out<"communications_get_message">>(`/communications/messages/${id}`, { signal }),
    updateMessage: (id: string, input: In<"communications_update_message">) =>
      patch<Out<"communications_update_message">>(`/communications/messages/${id}`, input),

    /** Platform staff only. Soft-deletes the phone message; the server retains its audit history. */
    deleteMessage: (id: string) => del<void>(`/communications/messages/${id}`),

    // --- calling and texting through the club's phone system

    /** What a phone number on a staff screen may offer this user: call, text, or neither (a plain tel: link). */
    capabilities: (signal?: AbortSignal) => get<Out<"communications_capabilities">>("/communications/capabilities", { signal }),
    /** Click to call: the PBX rings the user's own extension, then dials the number. Answers when the PBX accepted the request. */
    placeCall: (input: In<"communications_place_call">) => post<Out<"communications_place_call">>("/communications/calls/place", input),
    /** Send a text. Keep the same `idempotency_key` when retrying one text: it is never sent twice. */
    sendText: (input: In<"communications_send_text">) => post<Out<"communications_send_text">>("/communications/messages/sms", input),
    listConversations: (page: PageRequest & { unreadOnly?: boolean | undefined } = {}) =>
      get<Out<"communications_list_conversations">>("/communications/conversations", paged(page, { unread_only: page.unreadOnly })),
    /** The texts with one number, newest first. */
    getConversation: (number: string, page: PageRequest = {}) =>
      get<Out<"communications_get_conversation">>(`/communications/conversations/${numberPath(number)}`, paged(page)),
    markConversationRead: (number: string) =>
      post<Out<"communications_mark_conversation_read">>(`/communications/conversations/${numberPath(number)}/read`),
    unreadTexts: (signal?: AbortSignal) => get<Out<"communications_unread_texts">>("/communications/texts/unread", { signal }),
    /**
     * The live event stream (server-sent events) as an unread `Response`. `EventSource` cannot send the sign-in and club headers,
     * so the body is read with `fetch`. `lastEventId` resumes after that event.
     */
    openLive: (options: { lastEventId?: string | undefined; signal?: AbortSignal | undefined } = {}) =>
      http.request<Response>("GET", "/communications/live", {
        responseType: "stream",
        signal: options.signal,
        headers: { Accept: "text/event-stream", ...(options.lastEventId ? { "Last-Event-ID": options.lastEventId } : {}) },
      }),

    getSettings: (signal?: AbortSignal) => get<Out<"communications_get_settings">>("/communications/settings", { signal }),
    updateSettings: (input: In<"communications_update_settings">) => patch<Out<"communications_update_settings">>("/communications/settings", input),
  };
}
